/**
 * ============================================================================
 * TASKS.JS v6.0 - NEUES LAYOUT + INFO MODAL
 * ============================================================================
 *
 * ✅ v6.0: NEUES LAYOUT + INFO MODAL
 *          - ROW 1: Icon + Titel + Info-Button (ⓘ)
 *          - ROW 2: View Toggle + "Neue Aufgabe" Button
 *          - Info-Modal mit statischer Modul-Beschreibung
 *          - Einheitliches Layout wie Shopping-Modul
 *
 * ✅ v5.0: MOBILE DESIGN GUIDE
 *          - isMobile State mit Resize-Listener
 *          - Responsive Container (padding: 0 auf Mobile)
 *          - Touch-Targets min 44px
 *          - Einheitliche Abstände (8px Basis)
 *
 * ✅ v4.3.2: TITLE FALLBACK für alte Tasks
 *    - Sendet taskTitle an API für Fallback-Suche
 *    - Ermöglicht Sync für alte Tasks ohne source_task_id
 *
 * ✅ v4.3.1: ROUTE FIX - /task-link/ statt /by-task/
 *    - Route umbenannt um Konflikte mit /:id zu vermeiden
 *
 * ✅ v4.3: TASK-CALENDAR SYNC
 *    - Kalendereintrag wird mit Task-ID verknüpft (sourceTaskId)
 *    - Bei Task-Toggle wird Kalendereintrag als erledigt markiert
 *    - Durchgestrichene Darstellung im Kalender
 *
 * ✅ v4.2: CALENDAR CONFIRMATION DIALOG
 *    - Fragt Benutzer vor Kalendereintrag-Erstellung
 *
 * ✅ v4.1: AUTO CALENDAR INTEGRATION
 * 🔧 v4.0: TOKEN CONTEXT
 * ✅ v3.0: ANALYTICS TRACKING
 * ✅ v2.5: HOUSEHOLD_USERS ID FIX
 * ✅ v2.0: PLANIVIO REBRANDING
 *
 * @version 6.0.0 - Neues Layout + Info Modal
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useToken } from '../../context/TokenContext'; // ✅ v4: NEU!
import { useTranslation } from '../../context/I18nContext';
import { useTheme } from '../../context/ThemeContext';
import { useHousehold } from '../../context/HouseholdContext';
import { 
  LoadingIndicator, 
  Skeleton, 
  SkeletonGrid,
  EmptyState 
} from '../common/Loading';
import TasksOverview from './TasksOverview';
import CreateTaskModal from './CreateTaskModal';
import { invalidateCalendarCache } from '../calendar/calendarCache'; // ✅ v4.3: Cache invalidieren
import './Tasks.css';

// ============================================================================
// ✅ v3: ANALYTICS + API CONFIG
// ============================================================================
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const LOADING_DELAY_MS = 200;

console.log('✅ Tasks.js v6.0 loaded - NEUES LAYOUT + INFO MODAL 📋');

/**
 * ✅ v4.3: Erstellt Kalendereintrag MIT Task-Verknüpfung
 * - sourceTaskId: Verknüpft Kalendereintrag mit Task
 * - Bei Task-Erledigung wird Kalendereintrag durchgestrichen
 */
const createCalendarEntryForTask = async (token, taskData) => {
  console.log('📅 createCalendarEntryForTask called with:', taskData);

  if (!token) {
    console.warn('📅 No token for calendar entry creation');
    alert('❌ Kein Token verfügbar. Bitte erneut anmelden.');
    return;
  }

  if (!taskData || !taskData.title) {
    console.warn('📅 No task data or title');
    alert('❌ Keine Aufgabendaten vorhanden.');
    return;
  }

  try {
    // Datum: due_date oder heute
    let appointmentDate = taskData.due_date || taskData.dueDate;
    if (!appointmentDate) {
      appointmentDate = new Date().toISOString().split('T')[0];
    } else if (appointmentDate.includes('T')) {
      appointmentDate = appointmentDate.split('T')[0];
    }

    // ✅ v4.2: Uhrzeit - Fallback wenn leer
    const appointmentTime = taskData.time && taskData.time.trim() !== ''
      ? taskData.time
      : '18:00';

    const calendarData = {
      title: `📋 ${taskData.title}`,
      description: taskData.description || `Aufgabe: ${taskData.title}`,
      date: appointmentDate,
      time: appointmentTime,
      category: 'privat',
      person: taskData.person || taskData.assigned_to || 'alle',
      allDay: false,
      // ✅ v4.3: Task-ID für Verknüpfung mitsenden
      sourceTaskId: taskData.id || null,
      isCompleted: false
    };

    console.log('📅 Creating calendar entry for task:', calendarData.title);
    console.log('📅 Date:', calendarData.date, 'Time:', calendarData.time);
    console.log('📅 Source Task ID:', calendarData.sourceTaskId);

    const response = await fetch(`${API_BASE_URL}/api/calendar`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(calendarData)
    });

    if (response.ok) {
      const result = await response.json();
      console.log('✅ Calendar entry created:', result.appointment?.id, 'linked to task:', calendarData.sourceTaskId);

      // ✅ v4.3: Kalender-Cache invalidieren
      invalidateCalendarCache();

      // ✅ Visuelles Feedback
      alert(`✅ Kalendereintrag erstellt!\n\n📋 ${calendarData.title}\n📅 ${calendarData.date} um ${calendarData.time}`);
    } else {
      const errorData = await response.json();
      console.warn('⚠️ Calendar entry creation failed:', errorData.error || response.status);
      alert(`❌ Fehler beim Erstellen des Kalendereintrags:\n${errorData.error || 'Unbekannter Fehler'}`);
    }
  } catch (err) {
    console.error('❌ Calendar entry creation error:', err.message);
    alert(`❌ Fehler: ${err.message}`);
  }
};

/**
 * ✅ v4.3.2: Aktualisiert den Kalendereintrag einer Task (durchgestrichen wenn erledigt)
 * Route umbenannt zu /task-link/ um Konflikte mit /:id zu vermeiden
 * ✅ NEU: Sendet auch taskTitle als Fallback für alte Tasks ohne source_task_id
 */
const updateCalendarEntryForTask = async (token, taskId, isCompleted, taskTitle = null) => {
  if (!token || !taskId) return;

  try {
    console.log('📅 Updating calendar entry for task:', taskId, 'isCompleted:', isCompleted, 'title:', taskTitle);

    const response = await fetch(`${API_BASE_URL}/api/calendar/task-link/${taskId}/complete`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ isCompleted, taskTitle })  // ✅ v4.3.2: taskTitle für Fallback
    });

    if (response.ok) {
      const result = await response.json();
      console.log('✅ Calendar entry updated:', result.updated, 'entries');
      // Cache invalidieren damit Änderungen im Kalender sichtbar sind
      invalidateCalendarCache();
    } else {
      console.warn('⚠️ Calendar entry update failed');
    }
  } catch (err) {
    console.error('❌ Calendar entry update error:', err.message);
  }
};

/**
 * ✅ v3: Safe Track Function - nutzt /track/batch
 */
const trackAnalyticsEvent = async (token, eventName, metadata = {}) => {
  if (!token || !eventName) return;
  
  try {
    const event = {
      type: 'event',
      event_name: String(eventName).substring(0, 100),
      component: String(metadata.component || 'Tasks').substring(0, 100),
      metadata: metadata,
      timestamp: new Date().toISOString()
    };
    
    const response = await fetch(`${API_BASE_URL}/api/analytics/track/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ events: [event] })
    });
    
    if (response.ok) {
      console.log(`✅ Analytics tracked: ${eventName}`, metadata);
    } else {
      console.warn(`❌ Analytics failed: ${response.status}`, eventName);
    }
  } catch (err) {
    console.warn(`❌ Analytics error: ${eventName}`, err.message);
  }
};

// ============================================================================
// GLOBAL STATE + SHARED PROMISE (Anti-Flicker)
// ============================================================================
let GLOBAL_TASKS_CACHE = [];
let GLOBAL_HAS_LOADED = false;
let GLOBAL_LOADING_PROMISE = null;

// ============================================================================
// COMPONENT
// ============================================================================
const Tasks = ({ active }) => {
  
  // ✅ v4: useAuth NUR für isAuthenticated, NICHT für getAccessToken!
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  
  // ✅ v4: Token aus zentralem Context!
  const { token, loading: tokenLoading } = useToken();
  
  const { t } = useTranslation();
  const { isDark } = useTheme();
  
  // HouseholdContext für Mitglieder
  const { members: householdMembers, getMemberName } = useHousehold();

  // isMobile State ZUERST deklarieren (vor styles)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const styles = useMemo(() => getStyles(isDark, isMobile), [isDark, isMobile]);
  
  // ============================================================================
  // memberOptions mit household_users.id (INTEGER)
  // ============================================================================
  const memberOptions = useMemo(() => {
    const options = [
      {
        id: 'alle',
        value: 'alle',
        name: 'Alle',
        avatar: '👥',
        isMe: false
      }
    ];
    
    if (householdMembers && householdMembers.length > 0) {
      householdMembers.forEach(member => {
        const displayName = member.display_name && member.display_name !== 'Mitglied'
          ? member.display_name
          : member.email?.split('@')[0] || `User-${member.user_id?.substring(0, 6)}`;
        
        let avatar = '👤';
        if (member.role === 'owner') avatar = '👑';
        else if (member.role === 'admin') avatar = '⭐';
        
        options.push({
          id: member.id,
          value: member.id,
          user_id: member.user_id,
          name: displayName,
          avatar: avatar,
          email: member.email,
          role: member.role,
          isMe: member.is_me || false
        });
      });
    }
    
    console.log('👥 memberOptions erstellt:', options.map(o => ({ 
      name: o.name, 
      value: o.value,
      type: typeof o.value 
    })));
    return options;
  }, [householdMembers]);

  // ============================================================================
  // STATE
  // ============================================================================
  const [tasks, setTasks] = useState(() => GLOBAL_TASKS_CACHE);
  const [isLoading, setIsLoading] = useState(!GLOBAL_HAS_LOADED);
  const [showLoading, setShowLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(!GLOBAL_HAS_LOADED);
  const [error, setError] = useState(null);
  
  // View Mode
  const [viewMode, setViewMode] = useState('grid');
  
  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  // ✅ v4.2: Calendar Button in Edit Modal (kein Bestätigungsdialog mehr)
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // ✅ v6: Info Modal
  const [showInfo, setShowInfo] = useState(false);

  // ============================================================================
  // REFS
  // ============================================================================
  const loadingTimerRef = useRef(null);
  const isMountedRef = useRef(true);
  const isInitialLoadRef = useRef(!GLOBAL_HAS_LOADED);
  const hasTrackedViewRef = useRef(false);
  const searchTimeoutRef = useRef(null);

  // ============================================================================
  // ✅ v5: Mobile Responsive State
  // ============================================================================
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // FAB Event
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.module === 'tasks') {
        setEditingTask(null);
        setShowModal(true);
      }
    };
    window.addEventListener('planivio-create-new', handler);
    return () => window.removeEventListener('planivio-create-new', handler);
  }, []);

  // ============================================================================
  // ✅ v4: Track Mount/Unmount
  // ============================================================================
  useEffect(() => {
    isMountedRef.current = true;
    console.log('🔄 Tasks MOUNTED');

    return () => {
      isMountedRef.current = false;
      console.log('🔄 Tasks UNMOUNTING');
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // ✅ v4: Analytics tracken wenn Token verfügbar (aus Context!)
  useEffect(() => {
    if (token && !hasTrackedViewRef.current && active) {
      hasTrackedViewRef.current = true;
      console.log('🔐 Tasks: Using token from TokenContext');
      trackAnalyticsEvent(token, 'component_view', { 
        component: 'Tasks',
        tasksCount: GLOBAL_TASKS_CACHE.length,
        hasHousehold: householdMembers.length > 0
      });
    }
  }, [token, active, householdMembers.length]);

  // ============================================================================
  // ✅ v4: ANALYTICS HELPER - Verwendet Token aus Context
  // ============================================================================
  const trackEvent = useCallback(async (eventName, metadata = {}) => {
    if (!token) {
      console.warn('❌ No token available for analytics');
      return;
    }
    
    trackAnalyticsEvent(token, eventName, {
      component: 'Tasks',
      ...metadata
    });
  }, [token]);

  // ============================================================================
  // Sync to Global Cache
  // ============================================================================
  useEffect(() => {
    if (tasks.length > 0) {
      GLOBAL_TASKS_CACHE = tasks;
    }
  }, [tasks]);

  // ============================================================================
  // ✅ v4: FETCH TASKS - Verwendet Token aus Context
  // ============================================================================
  const fetchTasks = useCallback(async () => {
    console.log('📡 Fetching tasks from backend...');
    
    // ✅ v4: Token aus Context verwenden
    if (!token) {
      throw new Error('Kein Token verfügbar');
    }
    
    const response = await fetch(`${API_BASE_URL}/api/tasks/sync`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Fehler beim Laden der Aufgaben');
    }
    
    const data = await response.json();
    const tasksData = data.tasks || [];
    
    console.log('✅ Fetched', tasksData.length, 'tasks');
    
    return tasksData;
  }, [token]);

  // ============================================================================
  // LOAD DATA
  // ============================================================================
  const loadData = useCallback(async (forceRefresh = false) => {
    // ✅ v4: Warten auf Token aus Context
    if (!token) {
      console.log('⏳ Waiting for token from TokenContext...');
      return;
    }
    
    if (GLOBAL_HAS_LOADED && GLOBAL_TASKS_CACHE.length > 0 && !forceRefresh) {
      console.log('📦 Using cached tasks:', GLOBAL_TASKS_CACHE.length);
      if (isMountedRef.current) {
        setTasks([...GLOBAL_TASKS_CACHE]);
        setIsLoading(false);
        setIsInitialLoad(false);
        isInitialLoadRef.current = false;
      }
      return;
    }
    
    if (GLOBAL_HAS_LOADED && !forceRefresh) {
      console.log('📦 Already loaded (empty), not reloading');
      if (isMountedRef.current) {
        setTasks([...GLOBAL_TASKS_CACHE]);
        setIsLoading(false);
        setIsInitialLoad(false);
        isInitialLoadRef.current = false;
      }
      return;
    }
    
    if (GLOBAL_LOADING_PROMISE) {
      console.log('⏳ Waiting for existing load...');
      try {
        const result = await GLOBAL_LOADING_PROMISE;
        if (isMountedRef.current) {
          setTasks([...result]);
          setIsLoading(false);
          setIsInitialLoad(false);
          isInitialLoadRef.current = false;
        }
      } catch (err) {
        console.error('❌ Shared promise failed:', err);
        if (isMountedRef.current) {
          setError(err.message);
          setIsLoading(false);
          setIsInitialLoad(false);
          isInitialLoadRef.current = false;
        }
      }
      return;
    }
    
    console.log('🚀 Starting new load...');
    
    if (isMountedRef.current) {
      setIsLoading(true);
      setError(null);
      
      if (!isInitialLoadRef.current) {
        loadingTimerRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            setShowLoading(true);
          }
        }, LOADING_DELAY_MS);
      }
    }
    
    GLOBAL_LOADING_PROMISE = fetchTasks();
    
    try {
      const result = await GLOBAL_LOADING_PROMISE;
      
      GLOBAL_TASKS_CACHE = result;
      GLOBAL_HAS_LOADED = true;
      
      if (isMountedRef.current) {
        setTasks([...result]);
        setIsInitialLoad(false);
        isInitialLoadRef.current = false;
      }
      
    } catch (err) {
      console.error('❌ Load error:', err.message);
      
      trackEvent('tasks_load_error', { error: err.message });
      
      if (isMountedRef.current) {
        setError(err.message);
        setIsInitialLoad(false);
        isInitialLoadRef.current = false;
        
        if (GLOBAL_TASKS_CACHE.length > 0) {
          setTasks([...GLOBAL_TASKS_CACHE]);
        }
      }
    } finally {
      GLOBAL_LOADING_PROMISE = null;
      
      if (isMountedRef.current) {
        setIsLoading(false);
        setShowLoading(false);
      }
      
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    }
  }, [fetchTasks, trackEvent, token]);

  // ============================================================================
  // INITIAL LOAD - ✅ v4: Wartet auf Token aus Context
  // ============================================================================
  useEffect(() => {
    if (!active) return;
    
    if (GLOBAL_HAS_LOADED && GLOBAL_TASKS_CACHE.length > 0) {
      console.log('📦 Using global cache on mount:', GLOBAL_TASKS_CACHE.length);
      setTasks([...GLOBAL_TASKS_CACHE]);
      setIsLoading(false);
      setIsInitialLoad(false);
      isInitialLoadRef.current = false;
      return;
    }
    
    if (GLOBAL_HAS_LOADED) {
      console.log('📦 Already loaded, using cache');
      setTasks([...GLOBAL_TASKS_CACHE]);
      setIsLoading(false);
      setIsInitialLoad(false);
      isInitialLoadRef.current = false;
      return;
    }
    
    if (isAuthLoading || tokenLoading) {
      console.log('⏳ Auth/Token loading...');
      return;
    }
    
    if (!isAuthenticated) {
      console.log('🔒 Not authenticated');
      setIsLoading(false);
      setIsInitialLoad(false);
      isInitialLoadRef.current = false;
      return;
    }
    
    // ✅ v4: Warten auf Token aus Context
    if (!token) {
      console.log('⏳ Waiting for token from TokenContext...');
      return;
    }
    
    console.log('🎬 Starting initial load');
    loadData();
  }, [active, isAuthenticated, isAuthLoading, tokenLoading, token, loadData]);

  // ============================================================================
  // REFRESH HANDLERS - Mit Analytics
  // ============================================================================
  const handleRetry = useCallback(() => {
    console.log('🔄 Manual retry');
    
    trackEvent('tasks_retry', { type: 'manual' });
    
    GLOBAL_HAS_LOADED = false;
    GLOBAL_LOADING_PROMISE = null;
    setError(null);
    setIsInitialLoad(true);
    isInitialLoadRef.current = true;
    loadData(true);
  }, [loadData, trackEvent]);

  const handleSilentRefresh = useCallback(() => {
    console.log('🔄 Silent refresh');
    
    trackEvent('tasks_refresh', { type: 'silent' });
    
    GLOBAL_HAS_LOADED = false;
    GLOBAL_LOADING_PROMISE = null;
    loadData(true);
  }, [loadData, trackEvent]);

  // ============================================================================
  // MODAL HANDLERS - Mit Analytics
  // ============================================================================
  const handleOpenNewTask = useCallback(() => {
    console.log('✅ Opening new task modal');
    
    trackEvent('tasks_modal_open', { mode: 'create' });
    
    setEditingTask(null);
    setShowModal(true);
  }, [trackEvent]);
  
  const handleEditTask = useCallback((task) => {
    console.log('✅ Opening edit task modal:', task.id);
    
    trackEvent('tasks_modal_open', { 
      mode: 'edit',
      priority: task.priority,
      category: task.category
    });
    
    setEditingTask(task);
    setShowModal(true);
  }, [trackEvent]);
  
  const handleCloseModal = useCallback(() => {
    console.log('✅ Closing task modal');
    setShowModal(false);
    setEditingTask(null);
  }, []);

  // ============================================================================
  // ✅ v4.2: CALENDAR BUTTON HANDLER (Direkter Eintrag ohne Bestätigung)
  // ============================================================================
  const handleCreateCalendarEntry = useCallback((taskData) => {
    console.log('📅 Creating calendar entry for:', taskData.title);
    if (token && taskData) {
      createCalendarEntryForTask(token, taskData);
    }
  }, [token]);

  // ============================================================================
  // FILTER HANDLERS - Mit Analytics
  // ============================================================================
  const handleClearError = useCallback(() => setError(null), []);
  
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    if (value.length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        trackEvent('tasks_search', { 
          query_length: value.length
        });
      }, 500);
    }
  }, [trackEvent]);
  
  const handleCategoryChange = useCallback((e) => {
    const value = e.target.value;
    setFilterCategory(value);
    
    trackEvent('tasks_filter_category', { category: value });
  }, [trackEvent]);
  
  const handlePriorityChange = useCallback((e) => {
    const value = e.target.value;
    setFilterPriority(value);
    
    trackEvent('tasks_filter_priority', { priority: value });
  }, [trackEvent]);
  
  const handleStatusChange = useCallback((e) => {
    const value = e.target.value;
    setFilterStatus(value);
    
    trackEvent('tasks_filter_status', { status: value });
  }, [trackEvent]);

  const handleViewModeChange = useCallback((newMode) => {
    trackEvent('tasks_view_mode_change', { 
      from_mode: viewMode,
      to_mode: newMode 
    });
    
    setViewMode(newMode);
  }, [viewMode, trackEvent]);

  // ============================================================================
  // ✅ v4: SAVE TASK - Verwendet Token aus Context
  // ============================================================================
  const handleSaveTask = useCallback(async (taskData) => {
    console.log('💾 handleSaveTask STARTED:', taskData.title);
    console.log('👤 assigned_to received:', taskData.assigned_to || taskData.person);
    
    // ✅ v4: Token aus Context prüfen
    if (!token) {
      setError('Kein Token verfügbar');
      return;
    }
    
    const rawAssignedTo = taskData.assigned_to || taskData.person;
    const assignedTo = (!rawAssignedTo || rawAssignedTo === 'alle') 
      ? null 
      : parseInt(rawAssignedTo, 10);
    
    console.log('👤 assigned_to transformed:', assignedTo, '(type:', typeof assignedTo, ')');
    
    const backendData = {
      ...taskData,
      assigned_to: assignedTo,
      person: undefined,
      assignedTo: undefined
    };
    
    const isEdit = !!editingTask;
    
    if (!isEdit) {
      // CREATE
      const tempId = `temp-${Date.now()}`;
      const optimisticTask = {
        ...taskData,
        id: tempId,
        assigned_to: assignedTo,
        done: 0,
        created_at: new Date().toISOString()
      };
      
      GLOBAL_TASKS_CACHE = [optimisticTask, ...GLOBAL_TASKS_CACHE];
      GLOBAL_HAS_LOADED = true;
      console.log('🗄️ Cache updated IMMEDIATELY:', GLOBAL_TASKS_CACHE.length);
      
      setTasks([...GLOBAL_TASKS_CACHE]);
      setShowModal(false);
      setEditingTask(null);
      
      trackEvent('tasks_create', { 
        category: taskData.category,
        priority: taskData.priority,
        has_due_date: !!taskData.due_date,
        is_assigned: assignedTo !== null
      });
      
      // Hintergrund API call mit Token aus Context
      (async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/tasks`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(backendData)
          });
          
          if (response.ok) {
            const result = await response.json();
            const newTask = result.task || result;
            console.log('✅ Task saved to server:', newTask.id);

            GLOBAL_TASKS_CACHE = GLOBAL_TASKS_CACHE.map(t =>
              t.id === tempId ? newTask : t
            );

            if (isMountedRef.current) {
              setTasks([...GLOBAL_TASKS_CACHE]);
              // Kein automatischer Kalendereintrag mehr - User kann über Edit-Modal manuell erstellen
            }
          } else {
            throw new Error('Server error');
          }
        } catch (err) {
          console.error('❌ API error:', err);

          trackEvent('tasks_create_error', { error: err.message });
          
          GLOBAL_TASKS_CACHE = GLOBAL_TASKS_CACHE.filter(t => t.id !== tempId);
          if (isMountedRef.current) {
            setTasks([...GLOBAL_TASKS_CACHE]);
          }
        }
      })();
      
    } else {
      // EDIT
      const oldTask = editingTask;
      const updatedTask = { ...oldTask, ...taskData, assigned_to: assignedTo };
      
      GLOBAL_TASKS_CACHE = GLOBAL_TASKS_CACHE.map(t => 
        t.id === oldTask.id ? updatedTask : t
      );
      GLOBAL_HAS_LOADED = true;
      
      setTasks([...GLOBAL_TASKS_CACHE]);
      setShowModal(false);
      setEditingTask(null);
      
      trackEvent('tasks_edit', { 
        task_id: oldTask.id,
        category: taskData.category,
        priority: taskData.priority,
        priority_changed: oldTask.priority !== taskData.priority,
        due_date_changed: oldTask.due_date !== taskData.due_date
      });
      
      // Hintergrund API call mit Token aus Context
      (async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/tasks/${oldTask.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(backendData)
          });
          
          if (!response.ok) {
            throw new Error('Server error');
          }
          console.log('✅ Task updated on server');
        } catch (err) {
          console.error('❌ API error:', err);
          
          trackEvent('tasks_edit_error', { error: err.message });
          
          GLOBAL_TASKS_CACHE = GLOBAL_TASKS_CACHE.map(t => 
            t.id === oldTask.id ? oldTask : t
          );
          if (isMountedRef.current) {
            setTasks([...GLOBAL_TASKS_CACHE]);
          }
        }
      })();
    }
  }, [editingTask, trackEvent, token]);

  // ============================================================================
  // ✅ v4.3: TOGGLE HANDLER - Mit Kalender-Sync
  // ============================================================================
  const handleToggleTask = useCallback(async (task) => {
    console.log('✅ Toggling task:', task.id);

    // ✅ v4: Token aus Context prüfen
    if (!token) {
      setError('Kein Token verfügbar');
      return;
    }

    const oldDone = task.done;
    const newDone = task.done ? 0 : 1;
    const isCompleted = newDone === 1;

    GLOBAL_TASKS_CACHE = GLOBAL_TASKS_CACHE.map(t =>
      t.id === task.id ? { ...t, done: newDone } : t
    );
    GLOBAL_HAS_LOADED = true;

    setTasks([...GLOBAL_TASKS_CACHE]);

    trackEvent('tasks_toggle', {
      task_id: task.id,
      new_status: newDone ? 'done' : 'open',
      priority: task.priority,
      category: task.category
    });

    // Hintergrund API call mit Token aus Context
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/tasks/${task.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ done: newDone })
        });

        if (!response.ok) {
          throw new Error('Server error');
        }

        // ✅ v4.3.2: Kalendereintrag als erledigt/offen markieren (durchgestrichen)
        // Sendet auch taskTitle für Fallback bei alten Tasks ohne source_task_id
        console.log('✅ Task API success, now updating calendar for task:', task.id, 'title:', task.title);
        await updateCalendarEntryForTask(token, task.id, isCompleted, task.title);

      } catch (err) {
        console.error('❌ API error:', err);

        trackEvent('tasks_toggle_error', { error: err.message });

        GLOBAL_TASKS_CACHE = GLOBAL_TASKS_CACHE.map(t =>
          t.id === task.id ? { ...t, done: oldDone } : t
        );
        if (isMountedRef.current) {
          setTasks([...GLOBAL_TASKS_CACHE]);
        }
      }
    })();
  }, [trackEvent, token]);

  // ============================================================================
  // ✅ v4: DELETE HANDLER - Verwendet Token aus Context
  // ============================================================================
  const handleDeleteTask = useCallback(async (id) => {
    if (!window.confirm(t('tasks.confirmDelete') || 'Aufgabe wirklich löschen?')) return;
    
    console.log('🗑️ Deleting task:', id);
    
    // ✅ v4: Token aus Context prüfen
    if (!token) {
      setError('Kein Token verfügbar');
      return;
    }
    
    const deletedTask = GLOBAL_TASKS_CACHE.find(t => t.id === id);
    const deletedIndex = GLOBAL_TASKS_CACHE.findIndex(t => t.id === id);
    
    GLOBAL_TASKS_CACHE = GLOBAL_TASKS_CACHE.filter(t => t.id !== id);
    GLOBAL_HAS_LOADED = true;
    
    setTasks([...GLOBAL_TASKS_CACHE]);
    setShowModal(false);
    setEditingTask(null);
    
    trackEvent('tasks_delete', { 
      task_id: id,
      category: deletedTask?.category,
      priority: deletedTask?.priority,
      was_done: deletedTask?.done === 1
    });
    
    // Hintergrund API call mit Token aus Context
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/tasks/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          console.log('✅ Task deleted on server');
        } else {
          throw new Error('Server error');
        }
      } catch (err) {
        console.error('❌ API error:', err);
        
        trackEvent('tasks_delete_error', { error: err.message });
        
        if (deletedTask) {
          const newCache = [...GLOBAL_TASKS_CACHE];
          newCache.splice(deletedIndex, 0, deletedTask);
          GLOBAL_TASKS_CACHE = newCache;
          if (isMountedRef.current) {
            setTasks([...GLOBAL_TASKS_CACHE]);
          }
        }
      }
    })();
  }, [t, trackEvent, token]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================
  const stats = useMemo(() => {
    const total = tasks.length;
    const completed = tasks.filter(t => t.done === 1).length;
    const open = total - completed;
    const highPriority = tasks.filter(t => t.priority === 'high' && !t.done).length;
    const todayTasks = tasks.filter(t => {
      if (!t.due_date) return false;
      const today = new Date().toISOString().split('T')[0];
      return t.due_date.split('T')[0] === today && !t.done;
    }).length;
    
    return { total, completed, open, highPriority, todayTasks };
  }, [tasks]);

  // ============================================================================
  // RENDER
  // ============================================================================
  if (!active) return null;

  if (!isAuthenticated && !isAuthLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.authRequired}>
          <span style={{ fontSize: '3rem' }}>🔒</span>
          <p style={{ margin: 0, fontWeight: 600 }}>{t('common.pleaseLogin')}</p>
        </div>
      </div>
    );
  }

  if ((isInitialLoad || tokenLoading) && tasks.length === 0 && !error) {
    return (
      <div style={styles.container}>
        <div style={styles.headerRow}>
          <div style={styles.headerLeft}>
            <Skeleton width={48} height={48} borderRadius={12} />
            <div>
              <Skeleton width={120} height={24} style={{ marginBottom: 8 }} />
              <Skeleton width={160} height={16} />
            </div>
          </div>
          <Skeleton width={160} height={44} borderRadius={12} />
        </div>
        
        <div style={styles.statsGrid}>
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} height={80} borderRadius={12} />
          ))}
        </div>
        
        <SkeletonGrid count={6} cardHeight={140} />
      </div>
    );
  }

  if (isAuthLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <span style={{ fontSize: '2rem' }}>🔐</span>
          <p>Authentifizierung...</p>
        </div>
      </div>
    );
  }

  if (error && tasks.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <span style={{ fontSize: '3rem' }}>⚠️</span>
          <p style={{ margin: 0, fontWeight: 600 }}>{error}</p>
          <button style={styles.retryBtn} onClick={handleRetry}>
            🔄 {t('common.retry') || 'Erneut versuchen'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      
      {showLoading && (
        <LoadingIndicator text="Aktualisiere..." position="fixed" />
      )}
      
      {error && tasks.length > 0 && (
        <div style={styles.errorBanner}>
          <span>⚠️ {error}</span>
          <button className="tasks-close-mini" onClick={handleClearError} style={styles.errorCloseBtn}>✕</button>
        </div>
      )}
      
      <div style={{
        opacity: showLoading ? 0.7 : 1,
        transition: 'opacity 0.2s ease',
        pointerEvents: showLoading ? 'none' : 'auto'
      }}>
        
        {/* ROW 1 - Icon + Title + Info Button */}
        <div className="tasks-header-row1">
          <div className="tasks-header-left">
            <div className="tasks-header-icon">✅</div>
            <div className="tasks-header-title-wrap">
              <h1 className="tasks-header-title">{t('tasks.title') || 'Aufgaben'}</h1>
              <span className="tasks-header-subtitle">{tasks.length} Aufgaben</span>
            </div>
            <button
              className="tasks-info-btn"
              onClick={() => setShowInfo(!showInfo)}
              title="Modul-Info"
            >
              ⓘ
            </button>
          </div>
        </div>

        {/* ✅ v6: Info Modal */}
        {showInfo && (
          <div style={styles.infoModal}>
            <div style={styles.infoModalHeader}>
              <span style={styles.infoModalTitle}>Aufgaben - Info</span>
              <button
                className="tasks-close-mini"
                style={styles.infoCloseBtn}
                onClick={() => setShowInfo(false)}
              >
                ✕
              </button>
            </div>
            <div style={styles.infoModalContent}>
              <p style={styles.infoText}>
                <strong>Aufgaben-Modul</strong> hilft dir dabei, deine täglichen Aufgaben
                zu organisieren und im Blick zu behalten. Erstelle Aufgaben, setze Prioritäten
                und verfolge deinen Fortschritt.
              </p>
              <ul style={styles.infoList}>
                <li>Erstelle und verwalte Aufgaben</li>
                <li>Setze Prioritäten (Hoch, Mittel, Niedrig)</li>
                <li>Kategorisiere nach Bereich (Arbeit, Haushalt, etc.)</li>
                <li>Weise Aufgaben Familienmitgliedern zu</li>
                <li>Verknüpfe mit dem Kalender</li>
                <li>Verfolge deinen Erledigungsfortschritt</li>
              </ul>
            </div>
          </div>
        )}

        {/* ROW 2 - View Toggle + Suche + Filter */}
        <div style={styles.controlsRow}>
          <div className="tasks-view-toggle">
            <button
              className={`tasks-view-btn${viewMode === 'grid' ? ' active' : ''}`}
              onClick={() => handleViewModeChange('grid')}
            >
              ▦ Karten
            </button>
            <button
              className={`tasks-view-btn${viewMode === 'list' ? ' active' : ''}`}
              onClick={() => handleViewModeChange('list')}
            >
              ☰ Liste
            </button>
          </div>

          <input
            type="text"
            placeholder="🔍 Suchen..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="tasks-search-inline"
            style={styles.searchInline}
          />

          <select
            value={`${filterCategory}|${filterPriority}|${filterStatus}`}
            onChange={(e) => {
              const [cat, prio, status] = e.target.value.split('|');
              setFilterCategory(cat);
              setFilterPriority(prio);
              setFilterStatus(status);
              trackEvent('tasks_filter_combined', { category: cat, priority: prio, status: status });
            }}
            className="tasks-filter-inline"
            style={styles.filterInline}
          >
            <optgroup label="Status">
              <option value="all|all|all">🔎 Alle</option>
              <option value="all|all|open">⏳ Offen</option>
              <option value="all|all|done">✅ Erledigt</option>
            </optgroup>
            <optgroup label="Priorität">
              <option value="all|high|all">🔴 Hoch</option>
              <option value="all|medium|all">🟡 Mittel</option>
              <option value="all|low|all">🟢 Niedrig</option>
            </optgroup>
            <optgroup label="Kategorie">
              <option value="personal|all|all">🏠 Persönlich</option>
              <option value="work|all|all">💼 Arbeit</option>
              <option value="household|all|all">🏡 Haushalt</option>
              <option value="shopping|all|all">🛒 Einkauf</option>
            </optgroup>
          </select>
        </div>

        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statIcon}>📋</div>
            <div style={styles.statInfo}>
              <div style={styles.statValue}>{stats.total}</div>
              <div style={styles.statLabel}>Gesamt</div>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statIcon, background: '#FEF3C7' }}>⏳</div>
            <div style={styles.statInfo}>
              <div style={styles.statValue}>{stats.open}</div>
              <div style={styles.statLabel}>Offen</div>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statIcon, background: '#D1FAE5' }}>✅</div>
            <div style={styles.statInfo}>
              <div style={{ ...styles.statValue, color: '#059669' }}>{stats.completed}</div>
              <div style={styles.statLabel}>Erledigt</div>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statIcon, background: '#FEE2E2' }}>🔴</div>
            <div style={styles.statInfo}>
              <div style={{ ...styles.statValue, color: '#DC2626' }}>{stats.highPriority}</div>
              <div style={styles.statLabel}>Hohe Priorität</div>
            </div>
          </div>
        </div>

        <TasksOverview
          tasks={tasks}
          onEditTask={handleEditTask}
          onToggleTask={handleToggleTask}
          searchQuery={searchQuery}
          filterCategory={filterCategory}
          filterPriority={filterPriority}
          filterStatus={filterStatus}
          viewMode={viewMode}
          setViewMode={handleViewModeChange}
          memberOptions={memberOptions}
          getMemberName={getMemberName}
          t={t}
          isDark={isDark}
        />
      </div>

      <CreateTaskModal
        show={showModal}
        onClose={handleCloseModal}
        editingTask={editingTask}
        onSave={handleSaveTask}
        onDelete={handleDeleteTask}
        onCreateCalendarEntry={handleCreateCalendarEntry}
        memberOptions={memberOptions}
        t={t}
        isDark={isDark}
      />

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

// ============================================================================
// STYLES
// ============================================================================
function getStyles(isDark, isMobile = false) {
  const colors = isDark ? {
    bg: '#111827',
    bgCard: '#1F2937',
    bgSecondary: '#374151',
    text: '#F9FAFB',
    textSecondary: '#D1D5DB',
    textMuted: '#9CA3AF',
    border: '#4B5563',
    borderLight: '#374151',
    inputBg: '#374151',
    accent: '#4A90A4',
    accentLight: '#6BA893'
  } : {
    bg: '#E8F4F8',
    bgCard: '#FFFFFF',
    bgSecondary: '#F0F9FF',
    text: '#1F2937',
    textSecondary: '#374151',
    textMuted: '#6B7280',
    border: '#B8D4DC',
    borderLight: '#D4E8EC',
    inputBg: '#F0F9FF',
    accent: '#4A90A4',
    accentLight: '#6BA893'
  };

  return {
    container: {
      padding: isMobile ? '5px' : '2rem 1.25rem',
      maxWidth: isMobile ? '100%' : '1400px',
      margin: '0 auto',
      width: '100%',
      background: colors.bg,
      minHeight: 'calc(100vh - 100px)',
      borderRadius: isMobile ? '0' : '20px',
      boxSizing: 'border-box',
      overflowX: 'hidden'
    },
    headerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: isMobile ? '0.75rem' : '1.5rem',
      padding: isMobile ? '0.75rem' : '0',
      flexWrap: 'wrap',
      gap: isMobile ? '0.75rem' : '1rem'
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '10px' : '12px',
      flex: 1
    },
    headerIcon: {
      width: isMobile ? '44px' : '48px',
      height: isMobile ? '44px' : '48px',
      background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentLight} 100%)`,
      borderRadius: isMobile ? '10px' : '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: isMobile ? '1.3rem' : '1.5rem',
      boxShadow: '0 4px 12px rgba(74, 144, 164, 0.3)'
    },
    headerTitle: {
      margin: 0,
      color: colors.text,
      fontSize: isMobile ? '1.3rem' : '1.5rem',
      fontWeight: 700,
      flex: 1
    },

    // ✅ v6.1: Info Button — Kalender-Design (44px Desktop, 32px Mobile)
    infoBtn: {
      width: isMobile ? '32px' : '44px',
      height: isMobile ? '32px' : '44px',
      background: isMobile ? 'transparent' : (isDark ? colors.bgCard : '#F8FAFC'),
      border: isMobile ? 'none' : `1px solid ${isDark ? colors.border : '#E2E8F0'}`,
      borderRadius: '12px',
      fontSize: isMobile ? '1rem' : '1.25rem',
      color: isDark ? colors.textMuted : '#475569',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s',
      marginLeft: isMobile ? '0.25rem' : '0.5rem',
      flexShrink: 0,
      boxSizing: 'border-box',
      position: 'relative',
      padding: 0,
      lineHeight: 1
    },

    // ✅ v6: Info Modal
    infoModal: {
      background: colors.bgCard,
      border: `2px solid ${colors.border}`,
      borderRadius: '16px',
      marginBottom: '1rem',
      marginLeft: isMobile ? '0.75rem' : '0',
      marginRight: isMobile ? '0.75rem' : '0',
      boxShadow: '0 4px 20px rgba(74, 144, 164, 0.15)',
      overflow: 'hidden'
    },
    infoModalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1rem 1.25rem',
      background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentLight})`,
      color: 'white'
    },
    infoModalTitle: {
      fontSize: '1rem',
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    infoCloseBtn: {
      background: 'rgba(255, 255, 255, 0.2)',
      border: 'none',
      borderRadius: '8px',
      width: '32px',
      height: '32px',
      fontSize: '1rem',
      color: 'white',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s',
      position: 'relative'
    },
    infoModalContent: {
      padding: '1.25rem'
    },
    infoText: {
      margin: '0 0 1rem 0',
      color: colors.textSecondary,
      fontSize: '0.9rem',
      lineHeight: 1.6
    },
    infoList: {
      margin: 0,
      padding: '0 0 0 1.25rem',
      color: colors.textMuted,
      fontSize: '0.875rem',
      lineHeight: 1.8
    },

    controlsRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      marginBottom: '1rem',
      padding: isMobile ? '0 0.75rem' : '0'
    },
    searchInline: {
      flex: '1 1 80px',
      height: '36px',
      padding: '0 12px',
      border: `1px solid #E2E8F0`,
      borderRadius: '100px',
      fontSize: '0.82rem',
      outline: 'none',
      background: colors.bgCard,
      color: colors.text,
      minWidth: 0
    },
    filterInline: {
      flex: '0 1 auto',
      height: '36px',
      padding: '0 12px',
      border: `1px solid #E2E8F0`,
      borderRadius: '100px',
      fontSize: '0.82rem',
      outline: 'none',
      background: colors.bgCard,
      color: colors.text,
      cursor: 'pointer',
      minWidth: 0,
      maxWidth: isMobile ? '120px' : 'none'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: isMobile ? '0.375rem' : '1rem',
      marginBottom: isMobile ? '0.5rem' : '1.5rem',
      padding: isMobile ? '0 0.75rem' : '0',
      width: '100%'
    },
    statCard: {
      background: colors.bgCard,
      border: `2px solid ${colors.border}`,
      borderRadius: isMobile ? '12px' : '16px',
      padding: isMobile ? '0.75rem' : '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '0.5rem' : '0.75rem',
      minHeight: isMobile ? '64px' : '72px',
      animation: 'staggerFadeIn 0.3s ease-out backwards'
    },
    statIcon: {
      width: isMobile ? '36px' : '40px',
      height: isMobile ? '36px' : '40px',
      borderRadius: isMobile ? '8px' : '10px',
      background: colors.borderLight,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: isMobile ? '1rem' : '1.1rem'
    },
    statInfo: {
      flex: 1,
      minWidth: 0
    },
    statValue: {
      fontSize: isMobile ? '1.25rem' : '1.5rem',
      fontWeight: 700,
      color: colors.text,
      lineHeight: 1
    },
    statLabel: {
      fontSize: isMobile ? '0.7rem' : '0.75rem',
      color: colors.textMuted,
      marginTop: '0.25rem',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    errorContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '400px',
      background: isDark ? '#450A0A' : '#FEF2F2',
      borderRadius: '16px',
      border: `2px solid ${isDark ? '#DC2626' : '#FECACA'}`,
      gap: '1rem',
      color: isDark ? '#FCA5A5' : '#991B1B'
    },
    errorBanner: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1rem',
      background: isDark ? '#450A0A' : '#FEF2F2',
      border: `2px solid ${isDark ? '#DC2626' : '#FECACA'}`,
      borderRadius: '12px',
      marginBottom: '1rem',
      color: isDark ? '#FCA5A5' : '#991B1B'
    },
    errorCloseBtn: {
      width: '32px',
      height: '32px',
      background: 'transparent',
      border: 'none',
      borderRadius: '8px',
      fontSize: '1rem',
      cursor: 'pointer',
      color: isDark ? '#FCA5A5' : '#991B1B',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      padding: 0,
      flexShrink: 0
    },
    retryBtn: {
      minHeight: '52px',
      padding: '0 28px',
      background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentLight} 100%)`,
      color: 'white',
      border: 'none',
      borderRadius: '14px',
      fontSize: '0.95rem',
      fontWeight: 600,
      cursor: 'pointer',
      boxShadow: '0 4px 16px rgba(74, 144, 164, 0.25)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem'
    },
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '400px',
      gap: '1rem',
      color: colors.textMuted
    },
    authRequired: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '400px',
      background: colors.bgCard,
      borderRadius: '16px',
      border: `2px solid ${colors.border}`,
      gap: '1rem',
      color: colors.textMuted
    }
  };
}

export default Tasks;



